import { useEffect, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { useNavigate } from "react-router-dom";
import { Tag } from "primereact/tag";
import expenseService from "../../services/ExpenseService";

export default function ExpenseListPage() {
    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const toast = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        loadExpenses();
    }, []);

    const loadExpenses = async () => {
        try {
            setLoading(true);
            const res = await expenseService.getAll();
            setExpenses(res.data || res);
        } catch (error) {
            toast.current.show({
                severity: "error",
                summary: "Hata",
                detail: "Giderler yüklenemedi!",
            });
        } finally {
            setLoading(false);
        }
    };

    const deleteExpense = async (id) => {
        try {
            const res = await expenseService.delete(id);
            const result = res.data || res;
            if (result.success === false) {
                toast.current.show({
                    severity: "error",
                    summary: "Hata",
                    detail: result.message || "Gider silinemedi!",
                });
                return;
            }
            toast.current.show({
                severity: "success",
                summary: "Başarılı",
                detail: "Gider silindi",
            });
            loadExpenses();
        } catch (error) {
            toast.current.show({
                severity: "error",
                summary: "Hata",
                detail: "Gider silinemedi!",
            });
        }
    };

    const confirmDelete = (event, id) => {
        confirmPopup({
            target: event.currentTarget,
            message: "Bu gideri silmek istediğinize emin misiniz?",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Sil",
            rejectLabel: "İptal",
            acceptClassName: "p-button-danger",
            accept: () => deleteExpense(id),
        });
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value);
    };

    const formatDate = (value) => {
        return new Date(value).toLocaleDateString('tr-TR');
    };

    const actionsTemplate = (rowData) => (
        <div className="flex gap-2 justify-center">
            <Button
                icon="pi pi-pencil"
                className="w-8 h-8 p-0 border-none bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded-lg transition-colors"
                onClick={() => navigate(`/expenses/edit/${rowData.id}`)}
                tooltip="Düzenle"
            />
            <Button
                icon="pi pi-trash"
                className="w-8 h-8 p-0 border-none bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                onClick={(e) => confirmDelete(e, rowData.id)}
                tooltip="Sil"
            />
        </div>
    );

    const recurringTemplate = (rowData) => {
        return rowData.isRecurring ?
            <Tag value="AYLIK" severity="info" className="rounded-lg" /> :
            <Tag value="TEK SEFERLİK" severity="secondary" className="rounded-lg" />;
    };

    return (
        <div className="p-2 md:p-4 animate-fade-in bg-[#f8fafc] min-h-screen">
            <Toast ref={toast}  appendTo={mounted ? document.body : "self"} />
            <ConfirmPopup />

            <div className="bg-white shadow-lg shadow-slate-200/50 border border-slate-200 rounded-2xl overflow-hidden">
                <div className="!bg-slate-200 dark:!bg-slate-800 border-b border-slate-200 dark:border-slate-700 p-4 md:p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <div className="!bg-red-500 p-4 rounded-xl shadow-md shadow-red-500/20">
                            <i className="pi pi-wallet text-white text-xl"></i>
                        </div>
                        <div>
                            <h1 className="text-xl md:text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Giderler</h1>
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className="px-2 py-0.5 bg-red-50 text-red-600 text-[9px] font-black rounded-lg uppercase border border-red-100">LİSTE</span>
                                <span className="text-slate-500 dark:text-slate-400 text-xs font-medium">• Şirket giderlerini takip edin</span>
                            </div>
                        </div>
                    </div>

                    <Button
                        label="YENİ GİDER EKLE"
                        icon="pi pi-plus"
                        className="!bg-red-500 border-none hover:!bg-red-600 rounded-xl px-6 py-3 font-black text-sm shadow-lg !shadow-red-500/20 transition-all hover:scale-[1.02]"
                        onClick={() => navigate("/expenses/add")}
                    />
                </div>

                <div className="p-4">
                    <DataTable
                        value={expenses}
                        paginator
                        rows={10}
                        loading={loading}
                        emptyMessage="Gider bulunamadı"
                        className="p-datatable-sm"
                        responsiveLayout="scroll"
                        rowHover
                        rowsPerPageOptions={[10, 20, 50]}
                    >
                        <Column
                            field="expenseDate"
                            header="TARİH"
                            body={(row) => <span className="text-slate-500">{formatDate(row.expenseDate)}</span>}
                            sortable
                            headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3 px-4"
                            className="py-3 px-4 font-medium"
                        />
                        <Column
                            field="expenseCategoryName"
                            header="BAŞLIK"
                            sortable
                            headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3 px-4"
                            className="py-3 px-4 font-bold text-slate-700"
                        />
                        <Column
                            field="description"
                            header="AÇIKLAMA"
                            headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3 px-4"
                            className="py-3 px-4 text-slate-600"
                        />
                        <Column
                            field="amount"
                            header="TUTAR"
                            body={(row) => <span className="font-bold text-red-600">{formatCurrency(row.amount)}</span>}
                            sortable
                            headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3 px-4"
                            className="py-3 px-4"
                        />
                        <Column
                            field="isRecurring"
                            header="DURUM"
                            body={recurringTemplate}
                            headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3 px-4"
                            className="py-3 px-4"
                        />
                        <Column
                            header="İŞLEMLER"
                            body={actionsTemplate}
                            headerClassName="bg-slate-50 text-slate-500 font-black text-[10px] uppercase py-3 px-4"
                            style={{ width: "120px", textAlign: "center" }}
                        />
                    </DataTable>
                </div>
            </div>
        </div>
    );
}
